import React, { useCallback, useEffect } from "react";
import clsx from "clsx";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { ScoreSlider } from "components/ui";
import { SCORE_THRESHOLD } from "../../TrackPageV3";
import { useInViews } from "utility/useInViews";

import styles from "./theme-card.module.scss";
import { ItemScore } from "apps/user/learner/api/types";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";

interface ThemeCardPropsType {
  id: string;
  name: string;
  label: string;
  /** Stable band identity for analytics — label is localised display text. */
  labelKey?: string | null;
  value: number;
  items: ItemScore[];
  onClick: (themeId: string) => void;
  className?: string;
  takenAt?: boolean;
  /** Stable domain identity. Never the display name, which is localised. */
  domainShorthand?: string;
  domainScore?: number | null;
  themePosition?: number;
  themeShorthand?: string;
}

const ThemeCard: React.FC<ThemeCardPropsType> = ({
  id,
  name,
  label,
  labelKey,
  value,
  items,
  onClick,
  className,
  takenAt = false,
  domainShorthand,
  domainScore,
  themePosition,
  themeShorthand,
}) => {
  const { t } = useTranslation("index");
  const { ref, inView } = useInViews();

  const goodItemsCount = items.filter(
    (item) => item.value >= SCORE_THRESHOLD
  ).length;
  const badItemsCount = items.filter(
    (item) => item.value < SCORE_THRESHOLD
  ).length;

  const handleClick = () => {
    trackEvent("Theme Card Clicked", {
      "Theme Score": value ?? null,
      "Theme Band": labelKey || null,
      "Domain Shorthand": domainShorthand,
      "Domain Score": domainScore ?? null,
      "Item Count": items.length,
      "Good Items Count": goodItemsCount,
      "Bad Items Count": badItemsCount,
      "Theme Position": themePosition,
      "Theme Shorthand": themeShorthand,
    });
    onClick(id);
  };

  const logImpression = useCallback(() => {
    trackEvent("Theme Card Impression", {
      "Theme Score": value ?? null,
      "Theme Band": labelKey || null,
      "Domain Shorthand": domainShorthand,
      "Domain Score": domainScore ?? null,
      "Item Count": items.length,
      "Good Items Count": goodItemsCount,
      "Bad Items Count": badItemsCount,
      "Theme Position": themePosition,
      "Theme Shorthand": themeShorthand,
    });
  }, [
    labelKey,
    value,
    items.length,
    domainShorthand,
    domainScore,
    themePosition,
    goodItemsCount,
    badItemsCount,
    themeShorthand,
  ]);
  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  return (
    <button
      className={clsx(styles["theme-row"], className)}
      onClick={handleClick}
      aria-label={`View details for ${name}`}
      disabled={!takenAt}
      ref={ref}
    >
      <div className={styles["left-section"]}>
        <span className={styles["theme-name"]}>{name}</span>
        <div>
          {takenAt ? (
            <div className={styles["theme-label-container"]}>
              <span className={styles["theme-label"]}>{label}</span>
              <div className={styles["item-badges"]}>
                {items.map((item) => {
                  if (item.value < SCORE_THRESHOLD) {
                    return (
                      <div
                        key={item.id}
                        className={styles["item-value-badge"]}
                        data-type="bad"
                      />
                    );
                  } else {
                    return (
                      <div
                        key={item.id}
                        className={styles["item-value-badge"]}
                        data-type="good"
                      />
                    );
                  }
                })}
              </div>
            </div>
          ) : (
            <p className={styles["theme-label-no-assessment"]}>
              {t("index:themeCard.notApplicable")}
            </p>
          )}
        </div>
      </div>

      <div className={styles["progress-section"]}>
        <ScoreSlider
          value={value}
          barColor="#eae1d7"
          textColor="var(--old-grey-700, #3a3a3a)"
          showScore
          showMarker={takenAt}
        />
      </div>

      <div className={styles["right-section"]}>
        <ChevronIcon className={styles.chevron} />
      </div>
    </button>
  );
};

export default ThemeCard;
